/**
 * bk-collection.js
 * ----------------
 * Progressive enhancement for collection pages:
 *   1. Price preset pills — click intercepted, grid updated via AJAX.
 *   2. Load More button — appends next page's products without reload.
 *
 * Both use Shopify Section Rendering API (`?section_id=...`) which
 * returns the rendered section HTML; we extract grid items + load-more
 * markup and swap them in.
 *
 * If JS is disabled or fails, links fall back to standard navigation.
 *
 * Vanilla JS, no dependencies. Event delegation so it survives DOM swaps.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR  = '[data-section-type="collection-template"]';
  var GRID_SELECTOR     = '.grid-flex.large-row';
  var COUNT_SELECTOR    = '.cc-filters-results__summary';
  var PILLS_SELECTOR    = '.bk-pills--price';
  var LOAD_MORE_ROOT    = '[data-bk-load-more-root]';
  var LOAD_MORE_BUTTON  = '[data-bk-load-more-button]';
  var PILL_SELECTOR     = '[data-bk-pill="price"]';

  function getSectionId() {
    var el = document.querySelector(SECTION_SELECTOR);
    return el ? el.getAttribute('data-section-id') : null;
  }

  /**
   * Fetch URL with section_id appended; return parsed Document.
   * Shopify returns just the section's HTML, evaluated against the
   * URL's filter/page params.
   */
  function fetchSection(url) {
    var sectionId = getSectionId();
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return fetch(u.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  /**
   * Replace the grid + load-more + pills + count with fresh markup.
   * Used when a filter changes (price pill click).
   */
  function replaceCollectionState(doc) {
    var pairs = [
      [GRID_SELECTOR,    GRID_SELECTOR],
      [LOAD_MORE_ROOT,   LOAD_MORE_ROOT],
      [PILLS_SELECTOR,   PILLS_SELECTOR],
      [COUNT_SELECTOR,   COUNT_SELECTOR]
    ];
    pairs.forEach(function (pair) {
      var fresh = doc.querySelector(pair[0]);
      var stale = document.querySelector(pair[1]);
      if (fresh && stale) {
        stale.replaceWith(fresh);
      } else if (!fresh && stale) {
        // Element no longer present in fresh markup (e.g., load-more
        // disappears when only one page of results remains).
        stale.remove();
      }
    });
    document.dispatchEvent(new CustomEvent('bk:collection:updated', {
      detail: { reason: 'filter-change' }
    }));
  }

  /**
   * Append fresh products from the next page to existing grid.
   * Used when Load More is clicked.
   */
  function appendNextPage(doc) {
    var freshGrid = doc.querySelector(GRID_SELECTOR);
    var oldGrid   = document.querySelector(GRID_SELECTOR);
    if (!freshGrid || !oldGrid) return;

    // Move children one by one (live HTMLCollection, so always index 0)
    while (freshGrid.children.length) {
      oldGrid.appendChild(freshGrid.children[0]);
    }

    // Replace load-more so its href now points to page+1, or remove
    // it if we're on the last page.
    var freshLM = doc.querySelector(LOAD_MORE_ROOT);
    var oldLM   = document.querySelector(LOAD_MORE_ROOT);
    if (freshLM && oldLM) {
      oldLM.replaceWith(freshLM);
    } else if (!freshLM && oldLM) {
      oldLM.remove();
    }

    document.dispatchEvent(new CustomEvent('bk:collection:updated', {
      detail: { reason: 'load-more' }
    }));
  }

  /* ---------------- Event handlers ---------------- */

  function onPillClick(e) {
    var pill = e.target.closest(PILL_SELECTOR);
    if (!pill) return;
    e.preventDefault();
    var href = pill.getAttribute('href');

    document.body.classList.add('bk-loading');
    fetchSection(href)
      .then(function (doc) {
        replaceCollectionState(doc);
        // Update URL, no reload
        history.pushState({ bkCollection: true }, '', href);
        // Scroll pills into view so user sees their new active state
        var pills = document.querySelector(PILLS_SELECTOR);
        if (pills) {
          var rect = pills.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            pills.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      })
      .catch(function (err) {
        console.error('[bk] pill fetch failed, falling back', err);
        window.location.href = href;
      })
      .finally(function () {
        document.body.classList.remove('bk-loading');
      });
  }

  function onLoadMoreClick(e) {
    var btn = e.target.closest(LOAD_MORE_BUTTON);
    if (!btn) return;
    e.preventDefault();
    var href = btn.getAttribute('href');

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    fetchSection(href)
      .then(function (doc) {
        appendNextPage(doc);
        // Update URL silently so refresh keeps the loaded state
        history.replaceState({ bkCollection: true }, '', href);
      })
      .catch(function (err) {
        console.error('[bk] load-more fetch failed, falling back', err);
        window.location.href = href;
      });
    // No .finally() — button is replaced by appendNextPage on success.
    // On failure, navigation happens, so state doesn't matter.
  }

  /**
   * Browser back/forward — simplest reliable behavior is full reload.
   * (Implementing AJAX restore would require caching DOM snapshots
   * per history entry; not worth complexity for v1.)
   */
  function onPopState(e) {
    if (e.state && e.state.bkCollection) {
      window.location.reload();
    }
  }

  function init() {
    document.addEventListener('click', onPillClick);
    document.addEventListener('click', onLoadMoreClick);
    window.addEventListener('popstate', onPopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
